<template>
  <view-page
    :title="pageTitle"
    permission-name="asset_reservation_management"
    :edit-button="false"
    :delete-button="false"
    :loading="loading"
    loading-text=""
  >
    <template v-slot:leftSide>
      <view-page-data-field
        v-if="!loading && fields.length > 0"
        v-for="field in fields"
        :key="field.label"
        :icon="field.icon"
        :label="field.label"
        :data="field.data"
      />
    </template>

    <template v-slot:headerRightSide>
      <management-tabs-and-search
        :tabs="tabs"
        :open-tab="openTab"
        @update-open-tab="openTab = $event"
      />
    </template>

    <template v-slot:rightSide>
      <v-tabs-items :value="openTab">
        <v-tab-item value="map">
          <reservation-details-map
            :asset-reservation-uuid="assetReservation.ar_uuid"
            :asset-reservation-geo-json="assetReservation.ar_geo_json"
            :asset-reservation-objects="
              assetReservation.asset_reservation_objects
            "
          />
        </v-tab-item>

        <v-tab-item value="associatedObjects">
          <reservation-objects-table
            :asset-reservation-objects="
              assetReservation.asset_reservation_objects
            "
            :loading="loading"
            @open-asset="openAsset"

          />
        </v-tab-item>
      </v-tabs-items>
    </template>
  </view-page>
</template>

<script>
import { mapActions } from "vuex";
import ViewPage from "@/components/Management/ViewPage/ViewPage";
import ViewPageDataField from "@/components/Management/ViewPage/ViewPageDataField";
import NewMap from "@/components/NewMap";
import ManagementTabsAndSearch from "@/components/_Organisms/ManagementTabsAndSearch";
import ReservationDetailsMap from "@/components/AssetReservationManagement/Data/ReservationDetailsMap";
import ReservationObjectsTable from "@/components/AssetReservationManagement/Data/ReservationObjectsTable";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";

export default {
  name: "AssetReservationManagementReservationDetails",

  components: {
    ReservationObjectsTable,
    ReservationDetailsMap,
    ManagementTabsAndSearch,
    NewMap,
    ViewPageDataField,
    ViewPage
  },

  mixins: [checkPermissionsMixin],


  data() {
    return {
      /**
       * Stores asset reservation data
       */
      assetReservation: {},

      /**
       * Determines which tab is being displayed.
       */
      openTab: "map",

      /**
       * Define if the page is loading
       */
      loading:false,
    };
  },

  computed: {
    reservationUuid() {
      return this.$route.params.uuid;
    },

    /**
     * Determines the title of the page
     * @returns {string}
     */
    pageTitle() {
      let title = this.$i18n.tc("AssetReservation", 1);
      if (this.assetReservation.ar_external_id) {
        title += ": " + this.assetReservation.ar_external_id;
      } else if (this.assetReservation.ar_id) {
        title += " " + this.assetReservation.ar_id;
      }
      return title;
    },

    /**
     * Define the values of the fields
     * {String} icon - Field icon
     * {String} label - Field label
     * {String} data - Field value
     * {Boolean} hasPermission - Determines if the user has permission to see the field.
     */
    fields() {
      return [
        {
          icon: "category",
          label: this.$i18n.tc("Template", 1),
          data: this.assetReservation.template_name
        },
        {
          icon: "assignment_turned_in",
          label: this.$i18n.t("State"),
          data: this.getAssetReservationState(this.assetReservation.state)
        },
        {
          icon: "person",
          label: this.$i18n.t("Creator"),
          data: this.assetReservation.creator_name
        },
        {
          icon: "mdi-calendar-plus",
          label: this.$i18n.t("CreationDate"),
          data: this.assetReservation.ar_created_at
        },
        {
          icon: "event_available",
          label: this.$i18n.t("AcceptanceDate"),
          data: this.assetReservation.ar_acceptance_date
        },
        {
          icon: "mdi-calendar-alert",
          label: this.$i18n.t("InstallationDeadline"),
          data: this.assetReservation.installation_deadline
        },
        {
          icon: "event_available",
          label: this.$i18n.t("InstallationDate"),
          data: this.assetReservation.ar_installation_date
        },
        {
          icon: "event_busy",
          label: this.$i18n.t("EndDate"),
          data: this.assetReservation.ar_current_end_date
        },
        {
          icon: "event_busy",
          label: this.$i18n.t("DroppedDate"),
          data: this.assetReservation.ar_dropped_date
        },
        {
          icon: "mdi-counter",
          label: this.$i18n.t("NumberActiveAssets"),
          data: this.assetReservation.number_active_assets
        },
        {
          icon: "notes",
          label: this.$i18n.tc("Comment", 2),
          data: this.assetReservation.ar_comment
        }
      ];
    },

    /**
     * Defines the tabs that appear on the left side
     * {String} value - That will be used to define which tab will be displayed
     * {String} text - Tab title
     * {Boolean} hasSearch - Defines if the tab will have the search bar
     */
    tabs() {
      return [
        {
          value: "associatedObjects",
          text: this.$i18n.tc("AssociatedObject", 2),
          disabled: !this.userHasFibercloudPermission(
            "asset_reservation_object_list"
          )
        },
        {
          value: "map",
          text: this.$i18n.tc("Map", 1)
        }
      ];
    }
  },

  mounted() {
    this.getAssetReservation();
  },

  methods: {
    ...mapActions(["fetchObjectByUUID"]),

    /**
     * Get asset reservation by the uuid that is defined in the current page route
     * @return {Promise<void>}
     */
    async getAssetReservation() {
      this.loading = true;
      try {
        let response = await this.fetchObjectByUUID({
          endpoint: "asset-reservations",
          objectUUID: this.reservationUuid,
          fieldsToInclude: null,
          fieldsToAppend: this.getFieldsToAppend()
        });

        this.assetReservation = response.data.data[0];
      } catch (e) {
        console.error(e);
        await this.$router.replace("/error");
      } finally {
        this.loading = false;
      }
    },

    /**
     * Get fields to append for each asset reservation
     * @returns {string}
     */
    getFieldsToAppend() {
      let fieldsToAppend = [
        "template_name",
        "installation_deadline",
        "number_active_assets",
        "state"
      ];
      if (this.userHasFibercloudPermission("asset_reservation_object_list")) {
        fieldsToAppend.push("asset_reservation_objects");
      }
      return fieldsToAppend.join(",");
    },

    /**
     * Push to the page to see the selected asset reservation
     * @param {Object} assetReservationObject - Selected asset reservation
     */
    openAsset(assetReservationObject) {
      /**
       * @property {String} name - Name of the route
       * @property {Object} params - Parameters needed for the route path
       */
      this.$router.push({
        name: "associated-objects",
        params: {
          uuid: this.reservationUuid,
          objectUuid: assetReservationObject.aro_object_uuid
        }
      });
    },

    /**
     * Returns the state of the selected asset reservation
     * @param state
     * @returns {*}
     */
    getAssetReservationState(state) {
      let stateDescription = state.main_description;
      stateDescription += state.secondary_description
        ? ", " + state.secondary_description
        : "";

      return stateDescription;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/AssetReservationManagement/Data/ReservationDetails.vue